import { useEffect, useRef, useState } from 'react';

import { useStore } from '@playbooks/contexts';
import { useAction, useMessage } from '@playbooks/hooks';
import { Button } from '@playbooks/ui/components/button';

const OAuthRedirectBtn = ({
	provider,
	callbackType,
	storageKey,
	url,
	params = {},
	popup = false,
	onNext = undefined,
	children = null,
	...props
}) => {
	const [authWindow, setAuthWindow] = useState(null);
	const callbackReceived = useRef(false);
	const store = useStore();

	// Hooks
	useMessage(
		event => {
			if (!popup || !authWindow || event.source !== authWindow || event.origin !== window.location.origin) return;
			if (event.data?.callbackType !== callbackType) return;
			callbackReceived.current = true;
			event.data.error ? onComplete({ error: event.data.error }) : onComplete({ data: event.data.data });
		},
		[authWindow, callbackType, popup],
	);

	useEffect(() => {
		if (!authWindow) return;
		const interval = window.setInterval(() => {
			if (authWindow.closed && !callbackReceived.current) {
				callbackReceived.current = true;
				onComplete({ error: `${provider} authorization was cancelled.` });
			}
		}, 500);
		return () => window.clearInterval(interval);
	}, [authWindow, provider]);

	// Actions
	const [onRedirect, redirectTask] = useAction(async () => {
		callbackReceived.current = false;
		const nextWindow = popup ? openPopup() : null;
		try {
			const response = await store.createRecord({
				url,
				data: { ...params, returnUrl: window.location.href },
			});
			const redirectUrl = response?.data?.url || response?.data?.authorizationUrl || response?.url;
			if (!redirectUrl) throw new Error(`${provider} redirect URL was not returned.`);
			if (nextWindow) return nextWindow.location.assign(redirectUrl);
			window.location.assign(redirectUrl);
		} catch (error) {
			nextWindow?.close();
			setAuthWindow(null);
			throw error;
		}
	});

	const [onComplete, completeTask] = useAction(async ({ data, error }) => {
		authWindow?.close();
		setAuthWindow(null);
		if (error) throw new Error(error);
		await onNext?.(data);
	});

	// Methods
	const openPopup = () => {
		const width = 600;
		const height = 700;
		const left = window.screenX + (window.outerWidth - width) / 2;
		const top = window.screenY + (window.outerHeight - height) / 2;
		const options = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`;
		const nextWindow = window.open('', '_blank', options);
		if (!nextWindow) throw new Error(`Please allow popups to connect ${provider}.`);
		nextWindow.sessionStorage.setItem(storageKey, JSON.stringify(params));
		setAuthWindow(nextWindow);
		return nextWindow;
	};

	// Render
	return (
		<Button isLoading={redirectTask.running || completeTask.running} onClick={onRedirect} {...props}>
			{children}
		</Button>
	);
};

export { OAuthRedirectBtn };
